import React, { FC, Suspense } from "react";

import { ErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import { useSessionStorage } from "react-use";

import { AddButton, SectionTitle, SpinLoader } from "@/components/atoms";
import { ContractPanel, InsuredGoodPanel } from "@/components/features";
import { TabPanel, Tabs } from "@/components/molecules/Tabs";
import ErrorCard from "@/components/organisms/ErrorCard/ErrorCard";
import { Permissions } from "@/shared/domain";
import { isABroker, useAuth, useGlobalFilter, useNavigation } from "@/shared/hooks";

import { StyledDashboardTab, StyledDashboardTabsPanel, StyledHomesection } from "../../styled";

const TabIndex = {
    INSURED_GOODS: 0,
    CONTRACTS: 1,
};

const QuickAccess: FC = () => {
    const { t } = useTranslation();
    const { policyholdersGF } = useGlobalFilter();
    const { user } = useAuth();
    const { navToTariffication } = useNavigation();
    const [selectedTab, setSelectedTab] = useSessionStorage<number>("quickAccessTab", TabIndex.INSURED_GOODS);

    if (!user.isAuthenticated) {
        return null;
    }
    const riskPermission = user.someHasPermission(Permissions.ViewRisks, policyholdersGF);
    const contractPermission = user.someHasPermission(Permissions.ViewContracts, policyholdersGF);

    const quickAccessTitle = () => {
        if (riskPermission && contractPermission) {
            return t("Common.QuickAccess");
        } else if (riskPermission) {
            return t("InsuredGoods.InsuredGoods");
        } else if (contractPermission) {
            return t("Common.Contracts");
        } else {
            return null;
        }
    };
    const displayQuickAccess = riskPermission || contractPermission;
    const displayQuickAccessTabs = riskPermission && contractPermission;

    return (
        <>
            {displayQuickAccess && (
                <StyledHomesection data-testid="home_section_quick_access">
                    <SectionTitle title={quickAccessTitle()}>
                        {user.someHasPermission(Permissions.AccessTariffication, policyholdersGF) && (
                            <AddButton
                                disabled={isABroker()}
                                label={t("Common.RequestAQuote")}
                                onClick={() => navToTariffication()}
                            />
                        )}
                    </SectionTitle>
                    {displayQuickAccessTabs && (
                        <Tabs value={selectedTab} onChange={(_event, tab) => setSelectedTab(tab)}>
                            <StyledDashboardTab
                                label={t("InsuredGoods.InsuredGoods")}
                                data-testid="dashboard_panel-tab-insured-goods"
                            />
                            <StyledDashboardTab
                                label={t("Common.Contracts")}
                                data-testid="dashboard_panel-tab-contracts"
                            />
                        </Tabs>
                    )}
                    <StyledDashboardTabsPanel $withoutTabs={!displayQuickAccessTabs}>
                        {riskPermission && (
                            <TabPanel
                                key={TabIndex.INSURED_GOODS}
                                data-testid="dashboard_panel-panel-insured-goods"
                                value={displayQuickAccessTabs ? selectedTab : TabIndex.INSURED_GOODS}
                                index={TabIndex.INSURED_GOODS}
                            >
                                <ErrorBoundary
                                    resetKeys={[`insuredGoods-${user.currentBrokerId}`]}
                                    FallbackComponent={ErrorCard}
                                >
                                    <Suspense fallback={<SpinLoader />}>
                                        <InsuredGoodPanel />
                                    </Suspense>
                                </ErrorBoundary>
                            </TabPanel>
                        )}

                        {contractPermission && (
                            <TabPanel
                                key={TabIndex.CONTRACTS}
                                data-testid="dashboard_panel-panel-contracts"
                                value={displayQuickAccessTabs ? selectedTab : TabIndex.CONTRACTS}
                                index={TabIndex.CONTRACTS}
                            >
                                <ErrorBoundary
                                    resetKeys={[`contracts-${user.currentBrokerId}`]}
                                    FallbackComponent={ErrorCard}
                                >
                                    <Suspense fallback={<SpinLoader />}>
                                        <ContractPanel />
                                    </Suspense>
                                </ErrorBoundary>
                            </TabPanel>
                        )}
                    </StyledDashboardTabsPanel>
                </StyledHomesection>
            )}
        </>
    );
};

export default QuickAccess;
